/**
 * Task Service
 * 
 * This service handles all operations related to tasks and boards.
 * It uses the API module for server communication and provides
 * additional error handling and state management.
 */

import * as api from './api';
import * as storage from './storage';
import { testBackendConnection } from './testConnection';
import config from '../../config';

// API status tracking
let apiStatus = {
  connected: false,
  lastCheck: null,
  connectionError: null
};

/**
 * Get the API URL
 * @returns {string} API URL
 */
export const getApiUrl = () => {
  return config.api.baseUrl;
};

/**
 * Initialize the service by testing the backend connection
 * @returns {Promise<boolean>} Whether connected to the backend
 */
export const initialize = async () => {
  try {
    // Test connection to backend
    console.log('Testing connection to backend...');
    const connected = await testBackendConnection();
    apiStatus.connected = connected;
    apiStatus.lastCheck = new Date();
    
    console.log(`Task Manager initialized with backend connection: ${apiStatus.connected}`);
    
    return apiStatus.connected;
  } catch (error) {
    console.error('Error initializing Task Manager service:', error);
    apiStatus.connected = false;
    apiStatus.connectionError = error.message || 'Failed to connect to the API';
    apiStatus.lastCheck = new Date();
    return false;
  }
};

/**
 * Force a new connection test to the backend
 * @returns {Promise<boolean>} Whether connected to the backend
 */
export const forceConnectionTest = async () => {
  return await initialize();
};

/**
 * Get the current connection status
 * @returns {boolean} Whether connected to the backend
 */
export const isBackendConnected = () => {
  return apiStatus.connected;
};

/**
 * Get the current storage mode (API)
 * @returns {string} Storage mode ('api')
 */
export const getStorageMode = () => {
  return 'api';
};

/**
 * Get connection status
 * @returns {Object} Connection status object
 */
export const getConnectionStatus = () => {
  return { ...apiStatus };
};

/**
 * Set connection status
 * @param {Object} status - New status values
 */
const setConnectionStatus = (status) => {
  apiStatus = {
    ...apiStatus,
    ...status,
    lastCheck: new Date()
  };
};

/**
 * API request handler that requires connection
 * @param {Function} apiFn - API function to call
 * @param {Array} args - Arguments to pass to the function
 * @returns {Promise<any>} Result of the function call
 */
const requireConnection = async (apiFn, ...args) => {
  if (!apiStatus.connected) {
    throw new Error('Not connected to backend');
  }
  
  try {
    return await apiFn(...args);
  } catch (error) {
    console.error('API request failed:', error);
    // Re-test connection on failure
    apiStatus.connected = await testBackendConnection();
    if (!apiStatus.connected) {
      throw new Error('Lost connection to backend');
    }
    throw error;
  }
};

// Tasks

/**
 * Get all tasks for a board
 * @param {string} boardId - Board ID
 * @returns {Promise<Array>} Array of tasks
 */
export const getTasks = async (boardId) => {
  try {
    const tasks = await api.getTasks(boardId);
    
    // Mark API as connected on success
    setConnectionStatus({
      connected: true,
      connectionError: null
    });
    
    return tasks;
  } catch (error) {
    console.error(`Error getting tasks for board ${boardId}:`, error);
    
    // Mark API as disconnected
    setConnectionStatus({
      connected: false,
      connectionError: error.message || "Failed to connect to the API"
    });
    
    // Return empty array instead of throwing to allow displaying empty columns
    return [];
  }
};

/**
 * Get a specific task
 * @param {string} id - Task ID
 * @returns {Promise<Object>} Task object
 */
export const getTask = async (id) => {
  try {
    const task = await api.getTask(id);
    
    // Mark API as connected on success
    setConnectionStatus({
      connected: true,
      connectionError: null
    });
    
    return task;
  } catch (error) {
    console.error(`Error getting task ${id}:`, error);
    
    // Mark API as disconnected
    setConnectionStatus({
      connected: false,
      connectionError: error.message || "Failed to connect to the API"
    });
    
    // Return empty object instead of throwing
    return {};
  }
};

/**
 * Create a new task
 * @param {Object} task - Task data
 * @returns {Promise<Object>} Created task
 */
export const createTask = async (task) => {
  try {
    const createdTask = await api.createTask(task);
    
    // Mark API as connected on success
    setConnectionStatus({
      connected: true,
      connectionError: null
    });
    
    return createdTask;
  } catch (error) {
    console.error("Error creating task:", error);
    
    // Mark API as disconnected
    setConnectionStatus({
      connected: false,
      connectionError: error.message || "Failed to connect to the API"
    });
    
    // Re-throw the error for handling by the caller
    throw error;
  }
};

/**
 * Update a task
 * @param {string} id - Task ID
 * @param {Object} updates - Task updates
 * @returns {Promise<Object>} Updated task
 */
export const updateTask = async (id, updates) => {
  try {
    const updatedTask = await api.updateTask(id, updates);
    
    // Mark API as connected on success
    setConnectionStatus({
      connected: true,
      connectionError: null
    });
    
    return updatedTask;
  } catch (error) {
    console.error(`Error updating task ${id}:`, error);
    
    // Mark API as disconnected
    setConnectionStatus({
      connected: false,
      connectionError: error.message || "Failed to connect to the API"
    });
    
    // Re-throw the error for handling by the caller
    throw error;
  }
};

/**
 * Delete a task
 * @param {string} id - Task ID
 * @returns {Promise<Object>} Response
 */
export const deleteTask = async (id) => {
  try {
    const response = await api.deleteTask(id);
    
    // Mark API as connected on success
    setConnectionStatus({
      connected: true,
      connectionError: null
    });
    
    return response;
  } catch (error) {
    console.error(`Error deleting task ${id}:`, error);
    
    // Mark API as disconnected
    setConnectionStatus({
      connected: false,
      connectionError: error.message || "Failed to connect to the API"
    });
    
    // Re-throw the error for handling by the caller
    throw error;
  }
};

/**
 * Move a task to a different column
 * @param {string} id - Task ID
 * @param {string} columnId - Column ID
 * @returns {Promise<Object>} Updated task
 */
export const moveTask = async (id, columnId) => {
  try {
    const updatedTask = await api.moveTask(id, columnId);
    
    // Mark API as connected on success
    setConnectionStatus({
      connected: true,
      connectionError: null
    });
    
    return updatedTask;
  } catch (error) {
    console.error(`Error moving task ${id} to column ${columnId}:`, error);
    
    // Mark API as disconnected
    setConnectionStatus({
      connected: false,
      connectionError: error.message || "Failed to connect to the API"
    });
    
    // Re-throw the error for handling by the caller
    throw error;
  }
};

// Boards

/**
 * Get all boards
 * @returns {Promise<Array>} Array of boards
 */
export const getBoards = async () => {
  try {
    const boards = await api.getBoards();
    
    // Mark API as connected on success
    setConnectionStatus({
      connected: true,
      connectionError: null
    });
    
    return boards;
  } catch (error) {
    console.error("Error getting boards:", error);
    
    // Mark API as disconnected
    setConnectionStatus({
      connected: false,
      connectionError: error.message || "Failed to connect to the API"
    });
    
    // Return empty array instead of throwing
    return [];
  }
};

/**
 * Get a specific board
 * @param {string} id - Board ID
 * @returns {Promise<Object>} Board object
 */
export const getBoard = async (id) => {
  try {
    const board = await api.getBoard(id);
    
    // Mark API as connected on success
    setConnectionStatus({
      connected: true,
      connectionError: null
    });
    
    return board;
  } catch (error) {
    console.error(`Error getting board ${id}:`, error);
    
    // Mark API as disconnected
    setConnectionStatus({
      connected: false,
      connectionError: error.message || "Failed to connect to the API"
    });
    
    // Return empty object instead of throwing
    return {};
  }
};

/**
 * Create a new board
 * @param {Object} board - Board data
 * @returns {Promise<Object>} Created board
 */
export const createBoard = async (board) => {
  try {
    const createdBoard = await api.createBoard(board);
    
    // Mark API as connected on success
    setConnectionStatus({
      connected: true,
      connectionError: null
    });
    
    return createdBoard;
  } catch (error) {
    console.error("Error creating board:", error);
    
    // Mark API as disconnected
    setConnectionStatus({
      connected: false,
      connectionError: error.message || "Failed to connect to the API"
    });
    
    // Re-throw the error for handling by the caller
    throw error;
  }
};

/**
 * Update a board
 * @param {string} id - Board ID
 * @param {Object} updates - Board updates
 * @returns {Promise<Object>} Updated board
 */
export const updateBoard = async (id, updates) => {
  try {
    const updatedBoard = await api.updateBoard(id, updates);
    
    // Mark API as connected on success
    setConnectionStatus({
      connected: true,
      connectionError: null
    });
    
    return updatedBoard;
  } catch (error) {
    console.error(`Error updating board ${id}:`, error);
    
    // Mark API as disconnected
    setConnectionStatus({
      connected: false,
      connectionError: error.message || "Failed to connect to the API"
    });
    
    // Re-throw the error for handling by the caller
    throw error;
  }
};

/**
 * Delete a board
 * @param {string} id - Board ID
 * @returns {Promise<Object>} Response
 */
export const deleteBoard = async (id) => {
  try {
    const response = await api.deleteBoard(id);
    
    // Mark API as connected on success
    setConnectionStatus({
      connected: true,
      connectionError: null
    });
    
    return response;
  } catch (error) {
    console.error(`Error deleting board ${id}:`, error);
    
    // Mark API as disconnected
    setConnectionStatus({
      connected: false,
      connectionError: error.message || "Failed to connect to the API"
    });
    
    // Re-throw the error for handling by the caller
    throw error;
  }
};

/**
 * Get user ID from storage or create one
 * @returns {string} User ID
 */
export const getUserId = () => {
  // Use a consistent temporary ID for API calls when not authenticated
  return 'temp-user-id';
}; 